import { useCallback } from 'react';
import { PIN_PROTECTION } from 'app/constants';

interface GracePeriodData {
  userId: number;
  timestamp: number;
}

export const usePinProtection = () => {
  // Check if user is within grace period (15 minutes after last validation)
  const checkGracePeriod = useCallback((userId: number): boolean => {
    try {
      const graceData = localStorage.getItem(
        PIN_PROTECTION.GRACE_PERIOD_STORAGE_KEY,
      );
      if (!graceData) return false;

      const { userId: storedUserId, timestamp }: GracePeriodData =
        JSON.parse(graceData);

      // Check if it's the same user
      if (storedUserId !== userId) return false;

      // Check if within grace period (15 minutes)
      const gracePeriodMs = PIN_PROTECTION.GRACE_PERIOD_SECONDS * 1000;
      const isWithinGracePeriod = Date.now() - timestamp < gracePeriodMs;

      return isWithinGracePeriod;
    } catch (error) {
      console.error('Error checking grace period:', error);
      // Clean up corrupted data
      localStorage.removeItem(PIN_PROTECTION.GRACE_PERIOD_STORAGE_KEY);
      return false;
    }
  }, []);

  // Save grace period after successful PIN validation
  const saveGracePeriod = useCallback((userId: number): void => {
    try {
      const graceData: GracePeriodData = {
        userId,
        timestamp: Date.now(),
      };
      localStorage.setItem(
        PIN_PROTECTION.GRACE_PERIOD_STORAGE_KEY,
        JSON.stringify(graceData),
      );
    } catch (error) {
      console.error('Error saving grace period:', error);
    }
  }, []);

  // Clear grace period (useful for logout or security reasons)
  const clearGracePeriod = useCallback((): void => {
    try {
      localStorage.removeItem(PIN_PROTECTION.GRACE_PERIOD_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing grace period:', error);
    }
  }, []);

  // Clean up expired grace period entries
  const cleanupExpiredGracePeriod = useCallback((): void => {
    try {
      const graceData = localStorage.getItem(
        PIN_PROTECTION.GRACE_PERIOD_STORAGE_KEY,
      );
      if (!graceData) return;

      const { timestamp }: GracePeriodData = JSON.parse(graceData);
      const gracePeriodMs = PIN_PROTECTION.GRACE_PERIOD_SECONDS * 1000;
      const isExpired = Date.now() - timestamp >= gracePeriodMs;

      if (isExpired) {
        localStorage.removeItem(PIN_PROTECTION.GRACE_PERIOD_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error cleaning up expired grace period:', error);
      localStorage.removeItem(PIN_PROTECTION.GRACE_PERIOD_STORAGE_KEY);
    }
  }, []);

  return {
    checkGracePeriod,
    saveGracePeriod,
    clearGracePeriod,
    cleanupExpiredGracePeriod,
  };
};
